import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:10000';


const STARTER_ICONS = ['⭐', '🦋', '🌙'];

export default function SettingsModal({ open, onClose, userName, onNameChange, userIcon, onUserIconChange, localMode = false, localDataBytes = 0, isPremium = false, onStorageModeChange }) {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const storageUsedMB = Math.round((localDataBytes / (1024 * 1024)) * 10) / 10;
  const storageCapLabel = isPremium ? 'Unlimited' : '20 MB';
  const [name, setName] = useState(userName || '');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [usernameTouched, setUsernameTouched] = useState(false);
  const [selectedIcon, setSelectedIcon] = useState(userIcon || '⭐');

  function changeStorageMode(nextLocal) {
    const MAX_FREE_SYNC_BYTES = 20 * 1024 * 1024;

    if (!nextLocal && localDataBytes > MAX_FREE_SYNC_BYTES && !isPremium) {
      const wantsUpgrade = window.confirm(
        'Your local workspace is over 20 MB. Syncing more than 20 MB requires a premium account. Upgrade first to enable sync. Press OK to sign in or create an account, or Cancel to stay in local-only mode.'
      );
      if (!wantsUpgrade) return;

      const token = localStorage.getItem('token');
      if (!token) {
        toast.info('Create an account or log in to upgrade your storage.');
        navigate('/login');
        return;
      }

      toast.info('Upgrade required before enabling sync.');
      return;
    }

    if (nextLocal) {
      const confirmed = window.confirm(
        'Use local-only mode? Your work will be saved only on this device and will not sync across devices. Local mode can use more than 20 MB, but data above the 20 MB sync limit cannot be automatically uploaded if you later enable sync unless you upgrade.'
      );
      if (!confirmed) return;
    }

    onStorageModeChange?.(nextLocal ? 'local' : 'sync');
  }

  async function callApi(body) {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_BASE}/api/auth/account`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error || 'Update failed');
    return data;
  }

  async function saveName(e) {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      await callApi({ name: name.trim() });
      localStorage.setItem('anotequest_username', name.trim());
      onNameChange(name.trim());
      toast.success('Name updated.');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  }

  function selectIcon(icon) {
    setSelectedIcon(icon);
    localStorage.setItem('anotequest_user_icon', icon);
    onUserIconChange?.(icon);
    toast.success('Icon updated!');
  }

  async function saveUsername(e) {
    e.preventDefault();
    if (!username.trim()) return;
    if (username.trim().length < 3) { toast.error('Username must be at least 3 characters.'); return; }
    if (!/^[a-zA-Z0-9_-]+$/.test(username.trim())) { toast.error('Username can only contain letters, numbers, dashes, and underscores.'); return; }

    setBusy(true);
    try {
      await callApi({ username: username.trim() });
      localStorage.setItem('anotequest_username', username.trim());
      toast.success('Username updated.');
      setUsername('');
      setUsernameTouched(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function savePassword(e) {
    e.preventDefault();
    if (password !== confirm) { toast.error('Passwords do not match.'); return; }
    if (password.length < 6) { toast.error('Password must be at least 6 characters.'); return; }
    setBusy(true);
    try {
      await callApi({ password });
      setPassword('');
      setConfirm('');
      toast.success('Password updated.');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Account settings</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="profile" className="mt-2">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="profile">Profile</TabsTrigger>
            <TabsTrigger value="security">Security</TabsTrigger>
            <TabsTrigger value="storage">Storage</TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="mt-4">
            <form className="space-y-4">
              <div className="flex flex-col items-center space-y-3">
                <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center text-4xl border-2 border-border">
                  {selectedIcon}
                </div>
                <p className="text-sm font-medium text-muted-foreground">Choose your icon</p>
                <div className="flex gap-3">
                  {STARTER_ICONS.map(icon => (
                    <button
                      key={icon}
                      type="button"
                      onClick={() => selectIcon(icon)}
                      className={`w-14 h-14 rounded-full text-3xl flex items-center justify-center border-2 transition-all ${
                        selectedIcon === icon
                          ? 'border-primary bg-primary/10 scale-110 shadow-md'
                          : 'border-border bg-muted/50 hover:border-primary/50 hover:scale-105'
                      }`}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">More icons unlock as you use the app</p>
              </div>

              <div className="border-t pt-4">
                <div className="space-y-1">
                  <label className="text-sm font-medium">Display name</label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    autoComplete="name"
                  />
                </div>
                <Button onClick={saveName} type="button" disabled={busy || !name.trim() || name.trim() === userName} className="w-full mt-2">
                  {busy ? 'Saving…' : 'Save name'}
                </Button>
              </div>

              <div className="border-t pt-4">
                <div className="space-y-1">
                  <label className="text-sm font-medium">Username</label>
                  <Input
                    value={username}
                    onChange={(e) => { setUsername(e.target.value); setUsernameTouched(true); }}
                    placeholder="Choose your username"
                    onBlur={() => setUsernameTouched(true)}
                  />
                  <p className="text-xs text-gray-500">Minimum 3 characters. Letters, numbers, dashes and underscores only.</p>
                  {usernameTouched && username && !/^[a-zA-Z0-9_-]+$/.test(username.trim()) && (
                    <p className="text-xs text-red-500">Username can only contain letters, numbers, dashes, and underscores.</p>
                  )}
                  {usernameTouched && username.trim().length > 0 && username.trim().length < 3 && (
                    <p className="text-xs text-red-500">Username must be at least 3 characters.</p>
                  )}
                </div>
                <Button onClick={saveUsername} type="button" disabled={busy || !username.trim() || username.trim().length < 3 || !/^[a-zA-Z0-9_-]+$/.test(username.trim())} className="w-full mt-2">
                  {busy ? 'Saving…' : 'Set username'}
                </Button>
              </div>
            </form>
          </TabsContent>

          <TabsContent value="security" className="mt-4">
            <form onSubmit={savePassword} className="space-y-4">
              <div className="space-y-1">
                <label className="text-sm font-medium">New password</label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Confirm new password</label>
                <Input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                />
              </div>
              <Button type="submit" disabled={busy || !password || !confirm} className="w-full">
                {busy ? 'Saving…' : 'Update password'}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="storage" className="mt-4 space-y-4">
            <div className="rounded-lg border p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-medium">Work locally on this device</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {localMode
                      ? 'Local-only is on. This workspace is not synced to the database.'
                      : 'Sync is on. Your workspace is backed up and available on your other devices.'}
                  </p>
                </div>
                <Switch checked={localMode} onCheckedChange={changeStorageMode} aria-label="Use local-only storage" />
              </div>
            </div>
            <div className="rounded-lg border border-border/80 bg-muted/10 p-3 text-sm">
              <div className="flex items-center justify-between gap-4">
                <span className="font-medium">Storage usage</span>
                <span className="font-semibold">{storageUsedMB} MB</span>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                {localMode
                  ? 'This workspace is currently stored locally on your device and is not limited by the 20 MB sync cap.'
                  : isPremium
                    ? `You are on a premium plan with unlimited synced storage.`
                    : `You are using ${storageUsedMB} MB of your free 20 MB synced storage.`}
              </p>
            </div>
            {localMode && localDataBytes > 20 * 1024 * 1024 && !isPremium && (
              <div className="rounded-lg border border-orange-400/50 bg-orange-50 p-3 text-sm text-orange-950 dark:bg-orange-950/30 dark:text-orange-100">
                <p className="font-medium">Large local workspace</p>
                <p className="mt-1">Your local workspace is currently {(localDataBytes / (1024 * 1024)).toFixed(1)} MB. Syncing more than 20 MB requires a premium account, so local-only mode must remain enabled until you upgrade.</p>
              </div>
            )}
            {localMode && !token && (
              <div className="rounded-lg border border-sky-400/50 bg-sky-50 p-4 text-sm text-slate-950 dark:bg-slate-900/80 dark:border-sky-500/50 dark:text-sky-100">
                <p className="font-medium">Sign up to save your local workspace</p>
                <p className="mt-1">Your current notes and work will remain on this device and can be synced once you create an account.</p>
                <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                  <Button className="w-full sm:w-auto" onClick={() => navigate('/login?mode=login')}>Log in</Button>
                  <Button variant="secondary" className="w-full sm:w-auto" onClick={() => navigate('/login?mode=signup')}>Create account</Button>
                </div>
              </div>
            )}
            {localMode ? (
              <div className="rounded-lg border border-amber-400/50 bg-amber-50 p-3 text-sm text-amber-950 dark:bg-amber-950/30 dark:text-amber-100">
              <p className="font-medium">Local-only warning</p>
                <p className="mt-1">Your data stays on this device and cannot sync across devices. There is no AnoteQuest 20 MB cap while local (it is limited by available device storage). If it grows beyond 20 MB, it will not be automatically uploaded when sync is turned back on unless you upgrade.</p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Synced workspaces include up to 20 MB of database storage. Switch to local-only mode for device-only storage.</p>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
